/**
 * OPS-11: Shift Briefing Panel
 *
 * Displays AI-generated shift guidance from OPS-9.
 * Shows highlights (operational facts) and recommended actions.
 */

import type { ShiftBriefing } from '../../services/controlTowerTypes';
import { InfoTooltip } from './InfoTooltip';

const PRIORITY_STYLE: Record<string, string> = {
  high:   'bg-rose-500/20 text-rose-400 border-rose-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low:    'bg-sky-500/20 text-sky-400 border-sky-500/30',
};

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-rose-400',
  warning:  'bg-amber-400',
  info:     'bg-sky-400',
};

interface ShiftBriefingPanelProps {
  briefing: ShiftBriefing;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function ShiftBriefingPanel({ briefing, collapsed, onToggleCollapse }: ShiftBriefingPanelProps) {
  return (
    <div className={`rounded-2xl bg-slate-800/50 border border-slate-700/50 p-5 shadow-lg${collapsed ? '' : ' min-h-[260px]'}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          AI Shift Briefing
          <InfoTooltip text="AI-generated operational insights summarizing the current shift's key events, risks, and recommended actions based on real-time hotel data." />
        </h2>
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand shift briefing' : 'Collapse shift briefing'}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            {collapsed ? '▸ Expand' : '▾ Collapse'}
          </button>
        )}
      </div>

      {/* Highlights */}
      {!collapsed && briefing.highlights.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-medium text-slate-500 uppercase mb-2">Highlights</h3>
          <div className="space-y-1.5">
            {briefing.highlights.map((h, i) => (
              <div key={`${h.type}-${i}`} className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-slate-900/40">
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${SEVERITY_DOT[h.severity] ?? 'bg-slate-500'}`} />
                <span className="text-xs text-slate-300">{h.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Actions */}
      {collapsed ? null : (() => {
        const actionable = briefing.recommendedActions.filter(r => r.type !== 'NO_ACTION_REQUIRED');
        const noAction = briefing.recommendedActions.find(r => r.type === 'NO_ACTION_REQUIRED');
        if (actionable.length > 0) return (
          <div>
            <h3 className="text-xs font-medium text-slate-500 uppercase mb-2">Recommended Actions</h3>
            <div className="space-y-2">
              {actionable.map((r, i) => (
                <div key={`${r.type}-${i}`} className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-slate-900/50 border border-slate-700/30">
                  <span className={`flex-shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border mt-0.5 ${PRIORITY_STYLE[r.priority] ?? PRIORITY_STYLE.low}`}>
                    {r.priority}
                  </span>
                  <span className="text-xs text-slate-300 leading-relaxed">{r.message}</span>
                </div>
              ))}
            </div>
          </div>
        );
        return (
          <div className="text-center py-4">
            <p className="text-slate-500 text-sm">{noAction?.message ?? 'No actions needed — all operations are smooth'}</p>
          </div>
        );
      })()}
    </div>
  );
}
